import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputAdornment,
    InputLabel,
    IconButton,
    Link,
    ListItemText,
    MenuItem,
    Popover,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import JoditEditor from 'jodit-react';
import 'jodit/es5/jodit.min.css';
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
    useMaterialReactTable,
} from 'material-react-table';
import { formatAppDateTime, formatNoteEditorTimestamp } from '../utils/dateFormats';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import { externalAssetService } from '../api/external-assets';
import { enumService } from '../api/enums';
import { flattenProductTypes, productTypeService } from '../api/product-types';
import { projectsService, ProjectItem } from '../api/projects';
import { ExternalAsset } from '../types/entities/external-asset.entity';
import { ExternalAssetRequest } from '../types/responses/external-assets.responses';
import { useFocusSyncToast } from '../utils/useFocusSyncToast';
import { MarkerHighlightTextField, MarkerTableText, renderTextWithMarkers, hasTextMarkers } from '../utils/textMarkerHighlight';
import {
    type FilePathEntry,
    parseFilePathEntries,
    toFilePathEditorValueFromEntries,
    toFilePathPayloadFromEntries,
    toFilePathPayloadValue,
} from '../utils/filePaths';
import { FilePathEntriesEditor } from './FilePathEntriesEditor';
import { RichHtmlEditorField } from './RichHtmlEditorField';

type NotesQuickColorKey = 'green' | 'orange' | 'red';

const DEFAULT_NEW_ASSET_FILE_PATH = 'I:\\My Drive\\FabAssets\\';

/** Prefill for new assets (HTML for Jodit). First line stays blank for quick notes. */
const EXTERNAL_ASSET_INFO_TEMPLATE = [
    '<p><br></p>',
    '<table style="width:100%;border-collapse:collapse;">',
    '<tbody>',
    '<tr>',
    '<td style="width:28%;padding:6px 8px;border:1px solid #ccc;vertical-align:top;"><strong>Recap</strong></td>',
    '<td style="padding:6px 8px;border:1px solid #ccc;vertical-align:top;"><br></td>',
    '</tr>',
    '<tr>',
    '<td style="padding:6px 8px;border:1px solid #ccc;vertical-align:top;"><strong>Hodnotenie</strong></td>',
    '<td style="padding:6px 8px;border:1px solid #ccc;vertical-align:top;">/10</td>',
    '</tr>',
    '<tr>',
    '<td style="padding:6px 8px;border:1px solid #ccc;vertical-align:top;"><strong>Pros</strong></td>',
    '<td style="padding:6px 8px;border:1px solid #ccc;vertical-align:top;"><br></td>',
    '</tr>',
    '<tr>',
    '<td style="padding:6px 8px;border:1px solid #ccc;vertical-align:top;"><strong>Cons</strong></td>',
    '<td style="padding:6px 8px;border:1px solid #ccc;vertical-align:top;"><br></td>',
    '</tr>',
    '</tbody>',
    '</table>',
].join('');

const createDefaultFilePathEntry = (): FilePathEntry => ({
    label: '5.7',
    path: DEFAULT_NEW_ASSET_FILE_PATH,
    primary: true,
});

const defaultNewAssetFilePathRows = (): FilePathEntry[] => [createDefaultFilePathEntry()];

const defaultNewAssetFilePathEditorValue = (): string =>
    toFilePathEditorValueFromEntries(defaultNewAssetFilePathRows());

const EXTERNAL_ASSET_STATUS_SUGGESTIONS = [
    'Owned',
    'AsiNepouzijem',
    'Wishlist',
    'Considering',
    'Archived',
] as const;

/** Mirrors API `App\Support\AssetTagParser::parse` for chip edit ↔ payload string. */
const parseAssetTagString = (raw: string | null | undefined): string[] => {
    if (raw == null) {
        return [];
    }
    const trimmed = raw.trim();
    if (!trimmed) {
        return [];
    }
    const parts = trimmed.split(/\s*[,;|]\s*|\r?\n+|\s+(?=#)/);
    const out: string[] = [];
    for (const part of parts) {
        const label = part.trim();
        if (label !== '') {
            out.push(label);
        }
    }
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const label of out) {
        if (!seen.has(label)) {
            seen.add(label);
            unique.push(label);
        }
    }
    return unique;
};

const serializeAssetTags = (tags: readonly string[]): string =>
    tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0).join(', ');

const MAX_EXTERNAL_ASSET_TITLE_LENGTH = 255;

const buildExternalAssetPageUrl = (assetId: string): string => {
    if (typeof window === 'undefined' || !assetId.trim()) {
        return '';
    }
    return `${window.location.origin}/external-assets?assetId=${encodeURIComponent(assetId.trim())}`;
};

const isHttpUrl = (value: string): boolean => {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

const normalizeDroppedUrl = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed) {
        return null;
    }

    try {
        const resolved = new URL(trimmed, window.location.origin).toString();
        return isHttpUrl(resolved) ? resolved : null;
    } catch {
        return null;
    }
};

const parseAnchorFromHtml = (html: string): { url: string; titleHint?: string } | null => {
    if (!html.trim() || typeof DOMParser === 'undefined') {
        return null;
    }

    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const anchor = doc.querySelector('a[href]');
        const href = anchor?.getAttribute('href')?.trim() ?? '';
        const url = normalizeDroppedUrl(href);
        if (!url) {
            return null;
        }

        const titleHint = anchor?.textContent?.replace(/\s+/g, ' ').trim() || undefined;
        return { url, titleHint };
    } catch {
        return null;
    }
};

const parseUriList = (uriList: string): string | null => {
    const firstUrlLine = uriList
        .split(/\r?\n/)
        .map((line) => line.trim())
        .find((line) => line.length > 0 && !line.startsWith('#'));

    return firstUrlLine ? normalizeDroppedUrl(firstUrlLine) : null;
};

const parseMozUrl = (mozUrl: string): { url: string; titleHint?: string } | null => {
    const lines = mozUrl
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    if (!lines.length) {
        return null;
    }

    const url = normalizeDroppedUrl(lines[0]);
    if (!url) {
        return null;
    }

    const titleHint = lines.slice(1).join(' ').trim() || undefined;
    return { url, titleHint };
};

const isUsefulLinkTitle = (titleHint: string, url: string): boolean => {
    const hint = titleHint.trim();
    if (!hint || hint === url) {
        return false;
    }
    if (isHttpUrl(hint)) {
        return false;
    }

    const normalizedHint = hint.replace(/^www\./i, '');
    try {
        const parsed = new URL(url);
        const hostPath = `${parsed.hostname}${parsed.pathname}`.replace(/^www\./i, '');
        if (normalizedHint === hostPath || normalizedHint === parsed.hostname.replace(/^www\./i, '')) {
            return false;
        }
    } catch {
        // keep hint
    }

    return true;
};

type LinkTransferPayload = {
    html?: string;
    plain?: string;
    uriList?: string;
    mozUrl?: string;
};

const linkTransferFromDataTransfer = (dataTransfer: DataTransfer): LinkTransferPayload => ({
    html: dataTransfer.getData('text/html') || undefined,
    plain: dataTransfer.getData('text/plain') || undefined,
    uriList: dataTransfer.getData('text/uri-list') || undefined,
    mozUrl: dataTransfer.getData('text/x-moz-url') || undefined,
});

/** Merge uri-list, HTML anchor, and plain text (same sources Jodit uses on paste). */
const parseLinkPayload = (payload: LinkTransferPayload): { url: string; titleHint?: string } | null => {
    let url: string | null = null;
    let titleHint: string | undefined;

    const fromHtml = parseAnchorFromHtml(payload.html ?? '');
    if (fromHtml) {
        url = fromHtml.url;
        titleHint = fromHtml.titleHint;
    }

    const fromMoz = parseMozUrl(payload.mozUrl ?? '');
    if (fromMoz) {
        url = fromMoz.url;
        if (!titleHint && fromMoz.titleHint) {
            titleHint = fromMoz.titleHint;
        }
    }

    const fromUriList = parseUriList(payload.uriList ?? '');
    if (fromUriList) {
        url = fromUriList;
    }

    const plain = (payload.plain ?? '').trim();
    if (plain) {
        const plainUrl = normalizeDroppedUrl(plain);
        if (plainUrl) {
            if (!url) {
                url = plainUrl;
            }
        } else if (isUsefulLinkTitle(plain, url ?? '')) {
            titleHint = plain;
        }
    }

    if (!url) {
        return null;
    }

    if (titleHint && !isUsefulLinkTitle(titleHint, url)) {
        titleHint = undefined;
    }

    return { url, titleHint };
};

const readLinkPayloadFromClipboard = async (): Promise<LinkTransferPayload | null> => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.read) {
        try {
            const items = await navigator.clipboard.read();
            const payload: LinkTransferPayload = {};

            for (const item of items) {
                for (const type of item.types) {
                    if (type === 'text/html' && !payload.html) {
                        payload.html = await (await item.getType(type)).text();
                    }
                    if (type === 'text/plain' && !payload.plain) {
                        payload.plain = await (await item.getType(type)).text();
                    }
                }
            }

            if (payload.html?.trim() || payload.plain?.trim()) {
                return payload;
            }
        } catch {
            // Fall through to readText().
        }
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
        const plain = await navigator.clipboard.readText();
        return plain.trim() ? { plain } : null;
    }

    return null;
};

const formatExternalLinkLabel = (url: string): string => {
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.replace(/^www\./i, '');
        const path = parsed.pathname === '/' ? '' : parsed.pathname;
        const combined = `${host}${path}`;
        return combined.length > 72 ? `${combined.slice(0, 69)}…` : combined;
    } catch {
        return url.length > 72 ? `${url.slice(0, 69)}…` : url;
    }
};

const looksLikePublisherUrlInput = (value: string): boolean => {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
        return false;
    }

    if (/^https?:\/\//i.test(trimmed)) {
        return true;
    }

    if (/^www\./i.test(trimmed)) {
        return true;
    }

    if (trimmed.includes('/')) {
        return true;
    }

    return /\.[a-z]{2,}(?:[/:?#]|$)/i.test(trimmed);
};

const resolvePublisherUrl = (value: string): string | null => {
    const trimmed = (value ?? '').trim();
    if (!trimmed || !looksLikePublisherUrlInput(trimmed)) {
        return null;
    }

    const withProtocol = trimmed.match(/^https?:\/\//i) ? trimmed : `https://${trimmed}`;
    return isHttpUrl(withProtocol) ? withProtocol : null;
};

/** Plain publisher name, or decoded path tail for seller/profile URLs (e.g. fab.com/sellers/…). */
const extractPublisherSearchTerm = (value: string): string => {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
        return '';
    }

    const resolvedUrl = resolvePublisherUrl(trimmed);
    if (resolvedUrl) {
        try {
            const parsed = new URL(resolvedUrl);
            const sellersMatch = parsed.pathname.match(/\/sellers\/(.+?)\/?$/i);
            if (sellersMatch?.[1]) {
                return decodeURIComponent(sellersMatch[1].replace(/\/$/, ''));
            }

            const segments = parsed.pathname.split('/').filter(Boolean);
            if (segments.length > 0) {
                return decodeURIComponent(segments[segments.length - 1]);
            }
        } catch {
            // Fall through to raw value.
        }
    }

    return trimmed;
};

type ExternalAssetsSearchMode = 'contains' | 'fuzzy' | 'startsWith';

const fuzzySubsequence = (haystack: string, needle: string): boolean => {
    const n = needle.trim().toLowerCase();
    if (!n) {
        return true;
    }

    const h = haystack.toLowerCase();
    let hi = 0;
    for (let i = 0; i < n.length; i += 1) {
        const ch = n[i];
        const idx = h.indexOf(ch, hi);
        if (idx === -1) {
            return false;
        }
        hi = idx + 1;
    }
    return true;
};

const getPublisherFilterParts = (value: string): string[] => {
    const raw = (value ?? '').trim();
    if (!raw) {
        return [];
    }

    const parts = new Set<string>([raw]);
    const extracted = extractPublisherSearchTerm(raw);
    if (extracted) {
        parts.add(extracted);
    }

    const resolvedUrl = resolvePublisherUrl(raw);
    if (resolvedUrl) {
        try {
            const parsed = new URL(resolvedUrl);
            parts.add(decodeURIComponent(parsed.pathname));
        } catch {
            // Ignore malformed URL segments.
        }
    }

    return Array.from(parts);
};

const buildPublisherFilterText = (value: string): string => getPublisherFilterParts(value).join(' ');

const publisherMatchesFilter = (
    publisherRaw: string,
    filterValue: string,
    mode: ExternalAssetsSearchMode,
): boolean => {
    const query = filterValue.trim();
    if (!query) {
        return true;
    }

    const queryLower = query.toLowerCase();
    return getPublisherFilterParts(publisherRaw).some((part) => {
        const partLower = part.toLowerCase();
        if (mode === 'startsWith') {
            return partLower.startsWith(queryLower);
        }
        if (mode === 'fuzzy') {
            return fuzzySubsequence(part, query);
        }
        return partLower.includes(queryLower);
    });
};

const titleFromDroppedLink = (url: string, titleHint?: string): string => {
    const hint = (titleHint ?? '').trim();
    if (isUsefulLinkTitle(hint, url)) {
        return hint.slice(0, MAX_EXTERNAL_ASSET_TITLE_LENGTH);
    }

    try {
        const parsed = new URL(url);
        const path = parsed.pathname === '/' ? '' : parsed.pathname;
        const derived = `${parsed.hostname}${path}`.trim();
        return (derived || url).slice(0, MAX_EXTERNAL_ASSET_TITLE_LENGTH);
    } catch {
        return url.slice(0, MAX_EXTERNAL_ASSET_TITLE_LENGTH);
    }
};

const LINK_DRAG_TYPE_HINTS = new Set([
    'text/uri-list',
    'text/plain',
    'text/html',
    'url',
    'text/url',
    'public.url',
    'text/x-moz-url',
]);

const isFileOnlyDragTransfer = (dataTransfer: DataTransfer): boolean => {
    const types = Array.from(dataTransfer.types);
    return types.length > 0 && types.every((type) => type === 'Files');
};

/** Accept any non-file drag (incl. Edge/Chromium link and tab drags). */
const shouldAcceptLinkDropTransfer = (dataTransfer: DataTransfer): boolean => {
    if (isFileOnlyDragTransfer(dataTransfer)) {
        return false;
    }

    const types = Array.from(dataTransfer.types).map((type) => type.toLowerCase());
    if (types.length === 0) {
        return true;
    }

    return types.some((type) => LINK_DRAG_TYPE_HINTS.has(type));
};

const resolveAssetProjectIds = (asset: Pick<ExternalAsset, 'project_ids' | 'used_in_projects'>): string[] => {
    if ((asset.project_ids ?? []).length > 0) {
        return (asset.project_ids ?? []).filter((id) => id.trim().length > 0);
    }

    return (asset.used_in_projects ?? [])
        .map((project) => project.id)
        .filter((id) => id.trim().length > 0);
};

const emptyExternalAssetPayload = (): ExternalAssetRequest => ({
    title: '',
    info: EXTERNAL_ASSET_INFO_TEMPLATE,
    file_path: defaultNewAssetFilePathEditorValue(),
    publisher: '',
    source: '',
    asset_type: 'auto',
    product_type_id: '',
    status: '',
    tags: '',
    projects: '',
    project_ids: [],
    url: '',
    notes: '',
});

/** Quick filters for the global search field (tags column and all columns). Extend as needed. */
const EXTERNAL_ASSETS_FREQUENT_SEARCH_TAGS = ['#Vesmir'] as const;

const NOTES_TABLE_PREVIEW_MAX_CHARS = 100;

const stripHtmlNotes = (html: string): string =>
    (html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const truncateNotesPlain = (plain: string, maxChars = NOTES_TABLE_PREVIEW_MAX_CHARS): string =>
    plain.length > maxChars ? `${plain.slice(0, maxChars)}…` : plain;

const NotesTablePreviewCell: React.FC<{ notesHtml: string }> = ({ notesHtml }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const plainText = stripHtmlNotes(notesHtml);
    const truncated = truncateNotesPlain(plainText);
    const open = Boolean(anchorEl);

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        clearCloseTimer();
        if (!plainText) {
            return;
        }
        setAnchorEl(event.currentTarget);
    }, [clearCloseTimer, plainText]);

    const scheduleClose = useCallback(() => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            setAnchorEl(null);
            closeTimerRef.current = null;
        }, 180);
    }, [clearCloseTimer]);

    useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

    if (!plainText) {
        return <>—</>;
    }

    return (
        <>
            <Box
                onMouseEnter={handleOpen}
                onMouseLeave={scheduleClose}
                onClick={(event) => event.stopPropagation()}
                sx={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    cursor: 'default',
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    lineHeight: 1.35,
                }}
            >
                {truncated}
            </Box>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                disableRestoreFocus
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ pointerEvents: 'none' }}
                slotProps={{
                    paper: {
                        onMouseEnter: clearCloseTimer,
                        onMouseLeave: scheduleClose,
                        sx: {
                            pointerEvents: 'auto',
                            maxWidth: 420,
                            maxHeight: 280,
                            overflow: 'auto',
                            p: 1.5,
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                        '& p': { m: 0, mb: 0.75 },
                        '& p:last-child': { mb: 0 },
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block',
                        },
                    }}
                    dangerouslySetInnerHTML={{ __html: notesHtml }}
                />
            </Popover>
        </>
    );
};

export const ExternalAssets: React.FC = () => {
    const FILTER_STORAGE_KEY = 'external-assets.table-filters.v1';
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery('(max-width:960px)');
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const dropZoneRef = useRef<HTMLDivElement | null>(null);
    const [isLinkDropOver, setIsLinkDropOver] = useState(false);
    const [isDropZoneActive, setIsDropZoneActive] = useState(false);
    const [isReadingClipboard, setIsReadingClipboard] = useState(false);
    const [horizontalScrollDirection, setHorizontalScrollDirection] = useState<-1 | 0 | 1>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
    const [notesPreviewMode, setNotesPreviewMode] = useState<'rendered' | 'raw'>('rendered');
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'updated_at', desc: true }]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [searchMode, setSearchMode] = useState<'contains' | 'fuzzy' | 'startsWith'>('contains');
    const [filtersHydrated, setFiltersHydrated] = useState(false);
    const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
    const [isClosingModal, setIsClosingModal] = useState(false);
    const [selected, setSelected] = useState<ExternalAsset | null>(null);
    const [errors, setErrors] = useState<{ title: string }>({ title: '' });
    const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoSaveResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const notesEditorRef = useRef<any>(null);
    const notesPreviewRef = useRef<HTMLDivElement | null>(null);
    const lastSavedPayloadRef = useRef<string>('');
    const handledAssetQueryRef = useRef<string | null>(null);
    const [notesPreviewSelection, setNotesPreviewSelection] = useState<{
        text: string;
        top: number;
        left: number;
    } | null>(null);
    const [filePathRows, setFilePathRows] = useState<FilePathEntry[]>(defaultNewAssetFilePathRows());
    const [formData, setFormData] = useState<ExternalAssetRequest>({
        title: '',
        info: EXTERNAL_ASSET_INFO_TEMPLATE,
        file_path: defaultNewAssetFilePathEditorValue(),
        publisher: '',
        source: '',
        asset_type: 'auto',
        product_type_id: '',
        status: '',
        tags: '',
        projects: '',
        project_ids: [],
        url: '',
        notes: '',
    });

    useEffect(() => {
        if (isModalOpen) {
            const assetTitle = (formData.title ?? '').trim();
            if (assetTitle) {
                document.title = assetTitle;
                return;
            }
        }
        document.title = t('externalAssets.title');
    }, [t, isModalOpen, formData.title]);

    const notesEditorConfig = useMemo(
        () => ({
            readonly: false,
            height: 260,
            toolbarAdaptive: false,
            showCharsCounter: false,
            showWordsCounter: false,
            showXPathInStatusbar: false,
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            defaultActionOnPaste: 'insert_clear_html' as const,
            buttons: [
                'bold',
                'italic',
                'underline',
                '|',
                'ul',
                'ol',
                '|',
                'link',
                'table',
                '|',
                'undo',
                'redo',
                '|',
                'source',
            ],
        }),
        []
    );

    const notesQuickPalette = useMemo(
        () => ({
            text: {
                green: '#1f5d2a',
                orange: '#8a4b00',
                red: '#8a1f1f',
            },
            background: {
                green: '#c8e6c9',
                orange: '#ffd8a8',
                red: '#f5c2c7',
            },
        }),
        [],
    );

    const notesQuickColorOptions = useMemo(
        () => [
            { key: 'green' as const, label: 'Green', hotkey: 'G' },
            { key: 'orange' as const, label: 'Orange', hotkey: 'O' },
            { key: 'red' as const, label: 'Red', hotkey: 'R' },
        ],
        [],
    );

    const updateFilePathRows = useCallback((rows: FilePathEntry[]) => {
        const normalizedRows = rows.length ? rows : [createDefaultFilePathEntry()];
        setFilePathRows(normalizedRows);
        setFormData((prev) => ({
            ...prev,
            file_path: toFilePathEditorValueFromEntries(normalizedRows),
        }));
    }, []);

    const previewCandidates = useMemo(() => {
        const notesHtml = (formData.notes ?? '').trim();
        if (!notesHtml || typeof window === 'undefined') {
            return [] as string[];
        }

        const normalizeToEmbedOrAbsolute = (inputUrl: string): string => {
            try {
                const parsed = new URL(inputUrl, window.location.origin);
                if (parsed.hostname.includes('youtube.com')) {
                    if (parsed.pathname.startsWith('/embed/')) {
                        return parsed.toString();
                    }

                    const videoId = parsed.searchParams.get('v');
                    if (videoId) {
                        return `https://www.youtube.com/embed/${videoId}`;
                    }
                }

                if (parsed.hostname.includes('youtu.be')) {
                    const videoId = parsed.pathname.split('/').filter(Boolean).pop();
                    if (videoId) {
                        return `https://www.youtube.com/embed/${videoId}`;
                    }
                }

                return parsed.toString();
            } catch {
                return inputUrl;
            }
        };

        try {
            const doc = new DOMParser().parseFromString(notesHtml, 'text/html');
            const iframeUrls = Array.from(doc.querySelectorAll('iframe[src]'))
                .map((node) => node.getAttribute('src')?.trim() ?? '')
                .filter(Boolean);

            const anchorUrls = Array.from(doc.querySelectorAll('a[href]'))
                .map((node) => node.getAttribute('href')?.trim() ?? '')
                .filter(Boolean);

            const unique = new Set<string>();
            [...iframeUrls, ...anchorUrls].forEach((rawUrl) => {
                unique.add(normalizeToEmbedOrAbsolute(rawUrl));
            });

            return Array.from(unique);
        } catch {
            return [] as string[];
        }
    }, [formData.notes]);

    const previewEmbedUrl = previewCandidates[selectedPreviewIndex] ?? '';

    const handleOpenPreviewLink = useCallback(() => {
        if (!previewCandidates.length) {
            return;
        }
        setSelectedPreviewIndex(0);
        setIsPreviewModalOpen(true);
    }, [previewCandidates]);

    const handleClosePreviewModal = useCallback(() => {
        setIsPreviewModalOpen(false);
    }, []);

    const hideNotesPreviewSelection = useCallback(() => {
        setNotesPreviewSelection(null);
    }, []);

    const normalizedFormUrl = useMemo(() => {
        const raw = (formData.url ?? '').trim();
        if (!raw) {
            return '';
        }

        return raw.match(/^https?:\/\//i) ? raw : `https://${raw}`;
    }, [formData.url]);

    const normalizedFormPublisherUrl = useMemo(
        () => resolvePublisherUrl(formData.publisher ?? '') ?? '',
        [formData.publisher],
    );

    const searchByPublisher = useCallback((publisherValue: string, options?: { closeModal?: boolean }) => {
        const term = extractPublisherSearchTerm(publisherValue);
        if (!term) {
            return;
        }

        setGlobalFilter('');
        setColumnFilters((previous) => {
            const withoutPublisher = previous.filter((filter) => filter.id !== 'publisher');
            return [...withoutPublisher, { id: 'publisher', value: term }];
        });

        if (options?.closeModal) {
            setIsModalOpen(false);
        }

        enqueueSnackbar(
            t('externalAssets.publisherSearchApplied', {
                defaultValue: 'Filtering by publisher: {{term}}',
                term,
            }),
            { variant: 'info' },
        );
    }, [enqueueSnackbar, t]);

    const publisherFilterFn = useCallback(
        (row: { original: ExternalAsset }, _columnId: string, filterValue: unknown) =>
            publisherMatchesFilter(row.original.publisher ?? '', String(filterValue ?? ''), searchMode),
        [searchMode],
    );

    const selectedAssetIdFromQuery = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('assetId') ?? '').trim();
    }, [location.search]);

    const selectedProjectIdFromQuery = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('projectId') ?? '').trim();
    }, [location.search]);

    useEffect(() => {
        if (selectedPreviewIndex >= previewCandidates.length) {
            setSelectedPreviewIndex(0);
        }
    }, [previewCandidates, selectedPreviewIndex]);

    useEffect(() => {
        hideNotesPreviewSelection();
    }, [formData.notes, hideNotesPreviewSelection, notesPreviewMode]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            setFiltersHydrated(true);
            return;
        }

        try {
            const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
            if (!raw) {
                return;
            }

            const parsed = JSON.parse(raw) as {
                columnFilters?: MRT_ColumnFiltersState;
                globalFilter?: string;
                searchMode?: 'contains' | 'fuzzy' | 'startsWith';
            };

            if (Array.isArray(parsed.columnFilters)) {
                setColumnFilters(parsed.columnFilters);
            }
            if (typeof parsed.globalFilter === 'string') {
                setGlobalFilter(parsed.globalFilter);
            }
            if (parsed.searchMode === 'contains' || parsed.searchMode === 'fuzzy' || parsed.searchMode === 'startsWith') {
                setSearchMode(parsed.searchMode);
            }
        } catch {
            // Ignore malformed persisted filter state.
        } finally {
            setFiltersHydrated(true);
        }
    }, [FILTER_STORAGE_KEY]);

    useEffect(() => {
        if (typeof window === 'undefined' || !filtersHydrated) {
            return;
        }

        window.localStorage.setItem(
            FILTER_STORAGE_KEY,
            JSON.stringify({
                columnFilters,
                globalFilter,
                searchMode,
            }),
        );
    }, [FILTER_STORAGE_KEY, columnFilters, globalFilter, filtersHydrated, searchMode]);

    const {
        data: assets = [],
        isError,
        isLoading,
        isRefetching,
        refetch,
        dataUpdatedAt,
    } = useQuery<ExternalAsset[]>({
        queryKey: ['external-assets-catalog'],
        queryFn: () => externalAssetService.getCatalog(),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const { data: availableProjects = [], isLoading: projectsCatalogLoading } = useQuery<ProjectItem[]>({
        queryKey: ['projects-catalog'],
        queryFn: () => projectsService.getCatalog(),
    });

    const { data: enumOptions } = useQuery({
        queryKey: ['api-enums'],
        queryFn: () => enumService.getAll(),
    });

    const { data: productTypeTree = [] } = useQuery({
        queryKey: ['product-types-tree'],
        queryFn: () => productTypeService.getTree(),
    });

    const productTypeOptions = useMemo(
        () => flattenProductTypes(productTypeTree),
        [productTypeTree],
    );

    const importTypeOptions = enumOptions?.externalAssetImportTypes ?? [];

    const statusOptions = useMemo(() => {
        const fromCatalog = assets
            .map((asset) => (asset.status ?? '').trim())
            .filter(Boolean);
        return Array.from(new Set([...EXTERNAL_ASSET_STATUS_SUGGESTIONS, ...fromCatalog])).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
    }, [assets]);

    useFocusSyncToast({
        dataUpdatedAt,
        isRefetching,
        message: 'External assets synced from another window',
    });

    const filteredAssets = useMemo(() => {
        if (!selectedProjectIdFromQuery) {
            return assets;
        }

        return assets.filter((asset) =>
            (asset.used_in_projects ?? []).some((project) => project.id === selectedProjectIdFromQuery)
        );
    }, [assets, selectedProjectIdFromQuery]);

    const tagAutocompleteOptions = useMemo(() => {
        const unique = new Set<string>();
        for (const asset of assets) {
            for (const tag of parseAssetTagString(asset.tags)) {
                unique.add(tag);
            }
        }
        return Array.from(unique).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [assets]);

    const createMutation = useMutation({
        mutationFn: (payload: ExternalAssetRequest) => externalAssetService.create(payload),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: ExternalAssetRequest }) =>
            externalAssetService.update(id, payload),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => externalAssetService.delete(id),
        onSuccess: () => {
            enqueueSnackbar(t('externalAssets.messages.deleteSuccess'), { variant: 'success' });
            setIsDeleteModalOpen(false);
            setSelected(null);
            queryClient.invalidateQueries({ queryKey: ['external-assets-catalog'] });
        },
        onError: () => {
            enqueueSnackbar(t('externalAssets.messages.deleteError'), { variant: 'error' });
        },
    });

    const createAssetFromLinkPayload = useCallback(async (transfer: LinkTransferPayload) => {
        if (createMutation.isPending || updateMutation.isPending || isModalOpen) {
            return;
        }

        const parsed = parseLinkPayload(transfer);
        if (!parsed) {
            enqueueSnackbar(t('externalAssets.dropLinkInvalid'), { variant: 'warning' });
            return;
        }

        const payload: ExternalAssetRequest = {
            ...emptyExternalAssetPayload(),
            title: titleFromDroppedLink(parsed.url, parsed.titleHint),
            url: parsed.url,
        };

        try {
            await createMutation.mutateAsync(payload);
            await queryClient.invalidateQueries({ queryKey: ['external-assets-catalog'] });
            enqueueSnackbar(t('externalAssets.messages.createSuccess'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('externalAssets.messages.createError'), { variant: 'error' });
        }
    }, [
        createMutation,
        enqueueSnackbar,
        isModalOpen,
        queryClient,
        t,
        updateMutation.isPending,
    ]);

    const createAssetFromDroppedLink = useCallback(async (dataTransfer: DataTransfer) => {
        await createAssetFromLinkPayload(linkTransferFromDataTransfer(dataTransfer));
    }, [createAssetFromLinkPayload]);

    const handlePasteFromClipboard = useCallback(async () => {
        if (createMutation.isPending || updateMutation.isPending || isModalOpen) {
            return;
        }

        setIsReadingClipboard(true);
        try {
            const transfer = await readLinkPayloadFromClipboard();
            if (!transfer) {
                enqueueSnackbar(t('externalAssets.clipboardEmpty'), { variant: 'warning' });
                return;
            }

            await createAssetFromLinkPayload(transfer);
        } catch {
            enqueueSnackbar(t('externalAssets.clipboardReadError'), { variant: 'error' });
        } finally {
            setIsReadingClipboard(false);
        }
    }, [
        createAssetFromLinkPayload,
        createMutation.isPending,
        enqueueSnackbar,
        isModalOpen,
        t,
        updateMutation.isPending,
    ]);

    const handleDropZonePaste = useCallback((event: React.ClipboardEvent) => {
        event.preventDefault();
        void createAssetFromLinkPayload(linkTransferFromDataTransfer(event.clipboardData));
    }, [createAssetFromLinkPayload]);

    const handleDropZoneContextMenu = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        void handlePasteFromClipboard();
    }, [handlePasteFromClipboard]);

    useEffect(() => {
        const resetDragUi = () => {
            setIsLinkDropOver(false);
            setIsDropZoneActive(false);
        };

        const onDocumentDragOver = (event: DragEvent) => {
            const { dataTransfer } = event;
            if (!dataTransfer || !shouldAcceptLinkDropTransfer(dataTransfer)) {
                return;
            }
            event.preventDefault();
            dataTransfer.dropEffect = 'copy';
            setIsLinkDropOver(true);

            const dropZone = dropZoneRef.current;
            if (dropZone && event.clientX !== 0 && event.clientY !== 0) {
                const rect = dropZone.getBoundingClientRect();
                const overZone = event.clientX >= rect.left
                    && event.clientX <= rect.right
                    && event.clientY >= rect.top
                    && event.clientY <= rect.bottom;
                setIsDropZoneActive(overZone);
            }
        };

        const onDocumentDrop = (event: DragEvent) => {
            const { dataTransfer } = event;
            if (!dataTransfer || !shouldAcceptLinkDropTransfer(dataTransfer)) {
                return;
            }
            event.preventDefault();
            resetDragUi();

            const dropZone = dropZoneRef.current;
            if (!dropZone) {
                return;
            }

            const rect = dropZone.getBoundingClientRect();
            const overZone = event.clientX >= rect.left
                && event.clientX <= rect.right
                && event.clientY >= rect.top
                && event.clientY <= rect.bottom;
            if (!overZone) {
                return;
            }

            void createAssetFromDroppedLink(dataTransfer);
        };

        const onDocumentDragEnd = () => {
            resetDragUi();
        };

        document.addEventListener('dragover', onDocumentDragOver, true);
        document.addEventListener('drop', onDocumentDrop, true);
        document.addEventListener('dragend', onDocumentDragEnd, true);

        return () => {
            document.removeEventListener('dragover', onDocumentDragOver, true);
            document.removeEventListener('drop', onDocumentDrop, true);
            document.removeEventListener('dragend', onDocumentDragEnd, true);
        };
    }, [createAssetFromDroppedLink]);

    const handleDropZoneDragOver = (event: React.DragEvent) => {
        if (!shouldAcceptLinkDropTransfer(event.dataTransfer)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
        setIsLinkDropOver(true);
        setIsDropZoneActive(true);
    };

    const selectedProjects = useMemo(() => {
        const setIds = new Set((formData.project_ids ?? []).map((id) => id.trim()).filter(Boolean));
        return availableProjects.filter((project) => setIds.has(project.id));
    }, [availableProjects, formData.project_ids]);

    const resetForm = () => {
        setFilePathRows(defaultNewAssetFilePathRows());
        setFormData({
            title: '',
            info: EXTERNAL_ASSET_INFO_TEMPLATE,
            file_path: defaultNewAssetFilePathEditorValue(),
            publisher: '',
            source: '',
            asset_type: 'auto',
            product_type_id: '',
            status: '',
            tags: '',
            projects: '',
            project_ids: [],
            url: '',
            notes: '',
        });
        setErrors({ title: '' });
        lastSavedPayloadRef.current = '';
        setAutoSaveStatus('idle');
    };

    const handleOpenCreate = () => {
        setSelected(null);
        resetForm();
        setIsModalOpen(true);
    };

    const handleOpenEdit = (asset: ExternalAsset) => {
        setSelected(asset);
        const parsedFilePathRows = parseFilePathEntries(asset.file_path ?? '');
        const nextFilePathRows = parsedFilePathRows.length ? parsedFilePathRows : [{ label: '', path: '' }];
        const editorFilePath = toFilePathEditorValueFromEntries(nextFilePathRows);
        setFilePathRows(nextFilePathRows);
        setFormData({
            title: asset.title ?? '',
            info: asset.info ?? '',
            file_path: editorFilePath,
            publisher: asset.publisher ?? '',
            source: asset.source ?? '',
            asset_type: asset.asset_type ?? 'auto',
            product_type_id: asset.product_type_id ?? '',
            status: asset.status ?? '',
            tags: asset.tags ?? '',
            projects: asset.projects ?? '',
            project_ids: resolveAssetProjectIds(asset),
            url: asset.url ?? '',
            notes: asset.notes ?? '',
        });
        setErrors({ title: '' });
        // Must match buildPayload() (rows → JSON with primary), not editor-line roundtrip.
        lastSavedPayloadRef.current = JSON.stringify({
            title: (asset.title ?? '').trim(),
            info: (asset.info ?? '').trim(),
            file_path: toFilePathPayloadFromEntries(nextFilePathRows),
            publisher: (asset.publisher ?? '').trim(),
            source: (asset.source ?? '').trim(),
            asset_type: (asset.asset_type ?? 'auto').trim(),
            product_type_id: (asset.product_type_id ?? '').trim(),
            status: (asset.status ?? '').trim(),
            tags: (asset.tags ?? '').trim(),
            projects: (asset.projects ?? '').trim(),
            project_ids: [...resolveAssetProjectIds(asset)].sort(),
            url: (asset.url ?? '').trim(),
            notes: (asset.notes ?? '').trim(),
        });
        setAutoSaveStatus('idle');
        setIsModalOpen(true);
    };

    const closeModalImmediately = () => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        if (autoSaveResetTimerRef.current) {
            clearTimeout(autoSaveResetTimerRef.current);
            autoSaveResetTimerRef.current = null;
        }
        setIsModalOpen(false);
        setSelected(null);
        setNotesPreviewMode('rendered');
        setSelectedPreviewIndex(0);
        setIsClosingModal(false);
        resetForm();
    };

    const handleOpenDelete = (asset: ExternalAsset) => {
        setSelected(asset);
        setDeleteConfirmationInput('');
        setIsDeleteModalOpen(true);
    };

    useEffect(() => {
        if (!selectedAssetIdFromQuery) {
            handledAssetQueryRef.current = null;
            return;
        }

        if (handledAssetQueryRef.current === selectedAssetIdFromQuery) {
            return;
        }

        const targetAsset = assets.find((asset) => asset.id === selectedAssetIdFromQuery);
        if (!targetAsset) {
            return;
        }

        handleOpenEdit(targetAsset);
        handledAssetQueryRef.current = selectedAssetIdFromQuery;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assets, selectedAssetIdFromQuery]);

    const validateForm = (): boolean => {
        const title = (formData.title ?? '').trim();
        if (!title) {
            setErrors({ title: t('validation.required') });
            return false;
        }
        setErrors({ title: '' });
        return true;
    };

    const buildPayload = (): ExternalAssetRequest => ({
        title: (formData.title ?? '').trim(),
        info: (formData.info ?? '').trim(),
        file_path: toFilePathPayloadFromEntries(filePathRows),
        publisher: (formData.publisher ?? '').trim(),
        source: (formData.source ?? '').trim(),
        asset_type: (formData.asset_type ?? 'auto').trim(),
        product_type_id: (formData.product_type_id ?? '').trim(),
        status: (formData.status ?? '').trim(),
        tags: (formData.tags ?? '').trim(),
        projects: (formData.projects ?? '').trim(),
        project_ids: [...(formData.project_ids ?? [])].sort(),
        url: (formData.url ?? '').trim(),
        notes: (formData.notes ?? '').trim(),
    });

    const buildPayloadFromData = (data: ExternalAssetRequest): ExternalAssetRequest => ({
        title: (data.title ?? '').trim(),
        info: (data.info ?? '').trim(),
        file_path: toFilePathPayloadValue(data.file_path ?? ''),
        publisher: (data.publisher ?? '').trim(),
        source: (data.source ?? '').trim(),
        asset_type: (data.asset_type ?? 'auto').trim(),
        product_type_id: (data.product_type_id ?? '').trim(),
        status: (data.status ?? '').trim(),
        tags: (data.tags ?? '').trim(),
        projects: (data.projects ?? '').trim(),
        project_ids: [...(data.project_ids ?? [])].sort(),
        url: (data.url ?? '').trim(),
        notes: (data.notes ?? '').trim(),
    });

    const persistAsset = async (
        payload: ExternalAssetRequest,
        options: {
            closeOnSuccess: boolean;
            showSuccessToast: boolean;
            isAutoSave: boolean;
            syncFormDataFromServer?: boolean;
        }
    ): Promise<boolean> => {
        if (!payload.title) {
            return false;
        }

        if (createMutation.isPending || updateMutation.isPending) {
            return false;
        }

        if (options.isAutoSave) {
            setAutoSaveStatus('saving');
        }

        try {
            const savedAsset = selected?.id
                ? await updateMutation.mutateAsync({ id: selected.id, payload })
                : await createMutation.mutateAsync(payload);

            const normalizedFilePathRows = parseFilePathEntries(savedAsset.file_path ?? '');
            const nextFilePathRows = normalizedFilePathRows.length
                ? normalizedFilePathRows
                : [{ label: '', path: '' }];
            const normalizedSavedPayload = {
                title: savedAsset.title ?? '',
                info: savedAsset.info ?? '',
                file_path: toFilePathEditorValueFromEntries(nextFilePathRows),
                publisher: savedAsset.publisher ?? '',
                source: savedAsset.source ?? '',
                asset_type: savedAsset.asset_type ?? 'auto',
                product_type_id: savedAsset.product_type_id ?? '',
                status: savedAsset.status ?? '',
                tags: savedAsset.tags ?? '',
                projects: savedAsset.projects ?? '',
                project_ids: resolveAssetProjectIds(savedAsset),
                url: savedAsset.url ?? '',
                notes: savedAsset.notes ?? '',
            };
            // Prefer the payload we just sent so autosave baseline matches buildPayload().
            lastSavedPayloadRef.current = JSON.stringify({
                title: payload.title,
                info: payload.info,
                file_path: payload.file_path,
                publisher: payload.publisher,
                source: payload.source,
                asset_type: payload.asset_type,
                product_type_id: payload.product_type_id,
                status: payload.status,
                tags: payload.tags,
                projects: payload.projects,
                project_ids: [...(payload.project_ids ?? [])].sort(),
                url: payload.url,
                notes: payload.notes,
            });
            setSelected(savedAsset);
            if (!options.isAutoSave && options.syncFormDataFromServer !== false) {
                setFilePathRows(nextFilePathRows);
                setFormData(normalizedSavedPayload);
            }

            await queryClient.invalidateQueries({ queryKey: ['external-assets-catalog'] });
            await queryClient.invalidateQueries({ queryKey: ['projects-catalog'] });

            if (options.showSuccessToast) {
                enqueueSnackbar(
                    selected?.id ? t('externalAssets.messages.updateSuccess') : t('externalAssets.messages.createSuccess'),
                    { variant: 'success' }
                );
            }

            if (options.closeOnSuccess) {
                closeModalImmediately();
            }

            if (options.isAutoSave) {
                setAutoSaveStatus('saved');
                if (autoSaveResetTimerRef.current) {
                    clearTimeout(autoSaveResetTimerRef.current);
                }
                autoSaveResetTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 1200);
            }

            return true;
        } catch {
            if (options.isAutoSave) {
                setAutoSaveStatus('error');
            } else {
                enqueueSnackbar(
                    selected?.id ? t('externalAssets.messages.updateError') : t('externalAssets.messages.createError'),
                    { variant: 'error' }
                );
            }
            return false;
        }
    };

    const requestCloseModal = useCallback(async () => {
        if (isClosingModal) {
            return;
        }

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }

        const payload = buildPayload();
        const payloadKey = JSON.stringify(payload);
        const hasUnsavedChanges = payload.title && payloadKey !== lastSavedPayloadRef.current;

        if (!hasUnsavedChanges) {
            closeModalImmediately();
            return;
        }

        setIsClosingModal(true);
        const saved = await persistAsset(payload, {
            closeOnSuccess: false,
            showSuccessToast: false,
            isAutoSave: true,
        });

        if (!saved) {
            setIsClosingModal(false);
            enqueueSnackbar(
                t('externalAssets.messages.updateError', { defaultValue: 'Failed to save before close.' }),
                { variant: 'error' },
            );
            return;
        }

        closeModalImmediately();
    }, [buildPayload, closeModalImmediately, enqueueSnackbar, isClosingModal, persistAsset, t]);

    const triggerAutoSaveNow = (dataOverride?: ExternalAssetRequest) => {
        if (!isModalOpen) {
            return;
        }

        const payload = dataOverride ? buildPayloadFromData(dataOverride) : buildPayload();
        if (!payload.title) {
            return;
        }

        const payloadKey = JSON.stringify(payload);
        if (payloadKey === lastSavedPayloadRef.current) {
            return;
        }

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }

        void persistAsset(payload, { closeOnSuccess: false, showSuccessToast: false, isAutoSave: true });
    };

    const applyNotesInlineStyle = useCallback(
        (styleType: 'color' | 'background-color', color: string) => {
            const editor = notesEditorRef.current as any;
            if (!editor?.selection?.insertHTML) {
                return;
            }

            const selection = editor.selection;
            const selectedHtml = typeof selection.html === 'function' ? selection.html() : '';
            const selectedText = selection.sel?.toString?.() ?? '';
            const hasSelection = Boolean(selectedHtml?.trim() || selectedText.trim());
            const content = (selectedHtml || selectedText).trim();

            const safeContent = content || 'text';
            editor.selection.insertHTML(`<span style="${styleType}: ${color};">${safeContent}</span>`);

            const nextNotes = typeof editor.value === 'string' ? editor.value : formData.notes;
            const nextData = { ...formData, notes: nextNotes };
            setFormData(nextData);

            if (hasSelection) {
                triggerAutoSaveNow(nextData);
            }
        },
        [formData, triggerAutoSaveNow],
    );

    const applyNotesQuickColor = useCallback(
        (target: 'text' | 'background', colorKey: NotesQuickColorKey) => {
            if (target === 'text') {
                applyNotesInlineStyle('color', notesQuickPalette.text[colorKey]);
                return;
            }

            applyNotesInlineStyle('background-color', notesQuickPalette.background[colorKey]);
        },
        [applyNotesInlineStyle, notesQuickPalette],
    );

    const insertNotesTimestamp = useCallback(() => {
        const editor = notesEditorRef.current as any;
        if (!editor?.selection?.insertHTML) {
            return;
        }

        const timestamp = formatNoteEditorTimestamp();
        editor.selection.insertHTML(`<span style="color:#40E0D0; font-size:8px;">${timestamp}</span>`);

        const nextNotes = typeof editor.value === 'string' ? editor.value : formData.notes;
        const nextData = { ...formData, notes: nextNotes };
        setFormData(nextData);
        triggerAutoSaveNow(nextData);
    }, [formData, triggerAutoSaveNow]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }

        const payload = buildPayload();
        if (!payload.title) {
            return;
        }

        const payloadKey = JSON.stringify(payload);
        if (payloadKey === lastSavedPayloadRef.current) {
            return;
        }

        if (createMutation.isPending || updateMutation.isPending) {
            return;
        }

        setAutoSaveStatus('pending');
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
            void persistAsset(payload, { closeOnSuccess: false, showSuccessToast: false, isAutoSave: true });
        }, 900);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, isModalOpen, selected?.id, createMutation.isPending, updateMutation.isPending]);

    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
            if (autoSaveResetTimerRef.current) {
                clearTimeout(autoSaveResetTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (horizontalScrollDirection === 0) {
            return;
        }

        const interval = window.setInterval(() => {
            const container = tableContainerRef.current;
            if (!container) {
                return;
            }
            container.scrollLeft += horizontalScrollDirection * 14;
        }, 16);

        return () => {
            window.clearInterval(interval);
        };
    }, [horizontalScrollDirection]);

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }
        const payload = buildPayload();
        await persistAsset(payload, { closeOnSuccess: true, showSuccessToast: true, isAutoSave: false });
    };

    const handleSubmitWithoutClose = async () => {
        if (!validateForm()) {
            return;
        }
        const payload = buildPayload();
        await persistAsset(payload, {
            closeOnSuccess: false,
            showSuccessToast: true,
            isAutoSave: false,
            syncFormDataFromServer: false,
        });
    };

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }

        const isNotesEditorFocused = (): boolean => {
            const editor = notesEditorRef.current as any;
            const active = document.activeElement as HTMLElement | null;
            if (!editor || !active) {
                return false;
            }
            const root: HTMLElement | null =
                (editor.container as HTMLElement | undefined) ??
                (editor.editor as HTMLElement | undefined)?.closest?.('.jodit-container') ??
                null;
            if (root) {
                return root.contains(active);
            }
            return !!(
                active.classList?.contains('jodit-wysiwyg') ||
                active.closest?.('.jodit-wysiwyg') ||
                active.closest?.('.jodit-container')
            );
        };

        const handleSaveShortcut = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();
            const isAltSave = event.altKey && key === 's';
            const isPrimarySave = (event.ctrlKey || event.metaKey) && key === 's';
            const isQuickColorKey = key === 'g' || key === 'o' || key === 'r';
            const isInsertTimestamp = event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey && key === 'q';
            const notesQuickColorMap: Record<'g' | 'o' | 'r', NotesQuickColorKey> = {
                g: 'green',
                o: 'orange',
                r: 'red',
            };
            const notesFocused = isNotesEditorFocused();

            if (isInsertTimestamp && notesFocused) {
                event.preventDefault();
                event.stopPropagation();
                insertNotesTimestamp();
                return;
            }

            if (
                event.altKey &&
                !event.ctrlKey &&
                !event.metaKey &&
                isQuickColorKey &&
                notesFocused
            ) {
                const colorKey = notesQuickColorMap[key as 'g' | 'o' | 'r'];
                event.preventDefault();
                event.stopPropagation();

                if (event.shiftKey) {
                    applyNotesQuickColor('text', colorKey);
                    return;
                }

                applyNotesQuickColor('background', colorKey);
                return;
            }

            if (!isAltSave && !isPrimarySave) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (createMutation.isPending || updateMutation.isPending || isClosingModal) {
                return;
            }

            void handleSubmitWithoutClose();
        };

        window.addEventListener('keydown', handleSaveShortcut);
        return () => {
            window.removeEventListener('keydown', handleSaveShortcut);
        };
    }, [
        applyNotesQuickColor,
        createMutation.isPending,
        handleSubmitWithoutClose,
        insertNotesTimestamp,
        isClosingModal,
        isModalOpen,
        updateMutation.isPending,
    ]);

    const isAutoSaveBusy =
        isClosingModal ||
        autoSaveStatus === 'pending' ||
        autoSaveStatus === 'saving' ||
        createMutation.isPending ||
        updateMutation.isPending;

    const handleDeleteConfirm = () => {
        if (!selected?.id) {
            return;
        }

        const selectedTitle = (selected.title ?? '').trim();
        const typedTitle = deleteConfirmationInput.trim();
        if (!selectedTitle || typedTitle !== selectedTitle) {
            return;
        }

        deleteMutation.mutate(selected.id);
    };

    const copyValueToClipboard = useCallback(
        async (value?: string | null) => {
            const text = (value ?? '').trim();
            if (!text) {
                return;
            }

            const preview = text.length > 120 ? `${text.slice(0, 117)}...` : text;

            try {
                await navigator.clipboard.writeText(text);
                enqueueSnackbar(
                    t('common.copiedToClipboardWithValue', {
                        value: preview,
                        defaultValue: 'Copied: {{value}}',
                    }),
                    { variant: 'success' },
                );
            } catch {
                enqueueSnackbar(t('common.copyFailed', { defaultValue: 'Failed to copy value' }), {
                    variant: 'error',
                });
            }
        },
        [enqueueSnackbar, t],
    );

    const updateNotesPreviewSelection = useCallback(() => {
        const container = notesPreviewRef.current;
        const selection = typeof window !== 'undefined' ? window.getSelection() : null;

        if (!container || !selection || selection.rangeCount === 0 || selection.isCollapsed) {
            setNotesPreviewSelection(null);
            return;
        }

        const selectedText = selection.toString().trim();
        if (!selectedText) {
            setNotesPreviewSelection(null);
            return;
        }

        const range = selection.getRangeAt(0);
        const commonAncestor = range.commonAncestorContainer;
        const targetNode = commonAncestor.nodeType === Node.TEXT_NODE ? commonAncestor.parentNode : commonAncestor;

        if (!(targetNode instanceof Node) || !container.contains(targetNode)) {
            setNotesPreviewSelection(null);
            return;
        }

        const rangeRect = range.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        if (!rangeRect.width && !rangeRect.height) {
            setNotesPreviewSelection(null);
            return;
        }

        const popupLeft = rangeRect.left - containerRect.left + container.scrollLeft + rangeRect.width / 2;
        const popupTop = rangeRect.top - containerRect.top + container.scrollTop - 12;

        setNotesPreviewSelection({
            text: selectedText,
            left: popupLeft,
            top: popupTop,
        });
    }, []);

    const openAssetInNewTab = useCallback((asset: ExternalAsset) => {
        if (!asset.id) {
            return;
        }

        const targetUrl = buildExternalAssetPageUrl(asset.id);
        if (!targetUrl) {
            return;
        }
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }, []);

    const columns = useMemo<MRT_ColumnDef<ExternalAsset>[]>(
        () => [
            {
                accessorKey: 'title',
                header: t('externalAssets.columns.title'),
                filterFn: searchMode,
                Cell: ({ cell }) => {
                    const value = (cell.getValue<string>() ?? '').trim();
                    if (!value) {
                        return '—';
                    }
                    return (
                        <Tooltip title={t('externalAssets.copyHint', { defaultValue: 'Click to copy' })}>
                            <Box
                                component="button"
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void copyValueToClipboard(value);
                                }}
                                sx={{
                                    width: '100%',
                                    border: 'none',
                                    background: 'transparent',
                                    p: 0,
                                    m: 0,
                                    font: 'inherit',
                                    color: 'primary.main',
                                    textAlign: 'left',
                                    cursor: 'copy',
                                    wordBreak: 'break-word',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
                                    {renderTextWithMarkers(value, 'table')}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'url',
                header: t('externalAssets.columns.url'),
                filterFn: searchMode,
                Cell: ({ cell }) => {
                    const value = (cell.getValue<string>() ?? '').trim();
                    if (!value || !isHttpUrl(value)) {
                        return value || '—';
                    }

                    return (
                        <Tooltip title={t('externalAssets.openLinkHint', { defaultValue: 'Open link' })}>
                            <Link
                                href={value}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                sx={{ wordBreak: 'break-all' }}
                            >
                                {formatExternalLinkLabel(value)}
                            </Link>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'file_path',
                header: t('externalAssets.columns.filePath'),
                filterFn: searchMode,
                muiTableHeadCellProps: {
                    sx: { backgroundColor: 'rgba(25, 118, 210, 0.07)' },
                },
                muiTableBodyCellProps: {
                    sx: { backgroundColor: 'rgba(25, 118, 210, 0.05)' },
                },
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>();
                    const entries = parseFilePathEntries(value ?? '');
                    const copyAllValue = entries.length
                        ? entries
                            .map((entry) => (entry.path ?? '').trim())
                            .filter(Boolean)
                            .join('\n')
                        : (value ?? '');
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {entries.length ? (
                                    <Stack spacing={0.25}>
                                        {entries.map((entry, idx) => {
                                            const rowLabel = (entry.label ?? '').trim();
                                            const rowPath = (entry.path ?? '').trim();
                                            const rowDisplay = rowLabel && rowPath ? `${rowLabel} ${rowPath}` : rowPath || rowLabel;
                                            const copyValue = rowPath || rowDisplay;
                                            const displayWithPrimary = entry.primary
                                                ? `★ ${rowDisplay}`
                                                : rowDisplay;
                                            const editedAt = entry.timestamp
                                                ? (formatAppDateTime(entry.timestamp) || entry.timestamp)
                                                : '';
                                            const tooltipParts = [
                                                copyValue ? t('externalAssets.copyHint', { defaultValue: 'Click to copy' }) : '',
                                                editedAt ? `Edited ${editedAt}` : '',
                                            ].filter(Boolean);
                                            return (
                                                <Tooltip
                                                    key={`${rowLabel}-${rowPath}-${idx}`}
                                                    title={tooltipParts.join(' · ')}
                                                >
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            void copyValueToClipboard(copyValue);
                                                        }}
                                                        sx={{
                                                            width: '100%',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            p: 0,
                                                            m: 0,
                                                            font: 'inherit',
                                                            color: entry.primary ? 'warning.main' : 'primary.main',
                                                            fontWeight: entry.primary ? 600 : 400,
                                                            textAlign: 'left',
                                                            cursor: copyValue ? 'copy' : 'default',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            '&:hover': {
                                                                textDecoration: copyValue ? 'underline' : 'none',
                                                            },
                                                        }}
                                                    >
                                                        {hasTextMarkers(displayWithPrimary)
                                                            ? renderTextWithMarkers(displayWithPrimary, 'table')
                                                            : (displayWithPrimary || '-')}
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })}
                                    </Stack>
                                ) : (
                                    <Tooltip title={value ? t('externalAssets.copyHint', { defaultValue: 'Click to copy' }) : ''}>
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                void copyValueToClipboard(value);
                                            }}
                                            sx={{
                                                border: 'none',
                                                background: 'transparent',
                                                p: 0,
                                                m: 0,
                                                font: 'inherit',
                                                color: 'primary.main',
                                                textAlign: 'left',
                                                cursor: value ? 'copy' : 'default',
                                                '&:hover': {
                                                    textDecoration: value ? 'underline' : 'none',
                                                },
                                            }}
                                        >
                                            {hasTextMarkers(value) ? renderTextWithMarkers(value, 'table') : (value || '-')}
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>

                            <Tooltip title={t('common.copy', { defaultValue: 'Copy' })}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            void copyValueToClipboard(copyAllValue);
                                        }}
                                        disabled={!copyAllValue?.trim()}
                                        aria-label="Copy all paths"
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                },
            },
            {
                accessorKey: 'notes',
                header: t('externalAssets.columns.notes'),
                filterFn: searchMode,
                Cell: ({ cell }) => (
                    <NotesTablePreviewCell notesHtml={cell.getValue<string>() ?? ''} />
                ),
            },
            {
                accessorKey: 'source',
                header: t('externalAssets.columns.source'),
                filterFn: searchMode,
            },
            {
                id: 'publisher',
                accessorFn: (row) => buildPublisherFilterText(row.publisher ?? ''),
                header: t('externalAssets.columns.publisher'),
                filterFn: publisherFilterFn,
                Cell: ({ row }) => {
                    const value = (row.original.publisher ?? '').trim();
                    if (!value) {
                        return '—';
                    }

                    const publisherUrl = resolvePublisherUrl(value);

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            {publisherUrl ? (
                                <Tooltip title={t('externalAssets.openLinkHint', { defaultValue: 'Open link' })}>
                                    <Link
                                        href={publisherUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(event) => event.stopPropagation()}
                                        sx={{ wordBreak: 'break-all', flex: 1, minWidth: 0 }}
                                    >
                                        {formatExternalLinkLabel(publisherUrl)}
                                    </Link>
                                </Tooltip>
                            ) : (
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <MarkerTableText text={value} />
                                </Box>
                            )}
                            <Tooltip
                                title={t('externalAssets.searchByPublisher', {
                                    defaultValue: 'Search assets by this publisher',
                                })}
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color={publisherUrl ? 'default' : 'primary'}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            searchByPublisher(value);
                                        }}
                                        aria-label={t('externalAssets.searchByPublisher', {
                                            defaultValue: 'Search assets by this publisher',
                                        })}
                                    >
                                        <SearchIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                },
            },
            {
                accessorKey: 'asset_type',
                header: t('externalAssets.columns.type'),
                filterFn: searchMode,
            },
            {
                accessorKey: 'updated_at',
                header: t('externalAssets.columns.updatedAt'),
                filterFn: searchMode,
                sortingFn: (rowA, rowB, columnId) => {
                    const parseTime = (value: unknown) => {
                        if (typeof value !== 'string' || !value.trim()) {
                            return 0;
                        }
                        const t = new Date(value).getTime();
                        return Number.isNaN(t) ? 0 : t;
                    };
                    return parseTime(rowA.getValue(columnId)) - parseTime(rowB.getValue(columnId));
                },
                Cell: ({ cell }) => {
                    const raw = cell.getValue<string>();
                    if (!raw?.trim()) {
                        return '—';
                    }
                    const d = new Date(raw);
                    return Number.isNaN(d.getTime()) ? raw : formatAppDateTime(d);
                },
            },
            {
                accessorKey: 'status',
                header: t('externalAssets.columns.status'),
                filterFn: searchMode,
            },
            {
                accessorFn: (row) => {
                    const linkedNames = (row.used_in_projects ?? [])
                        .map((project) => project.name.trim())
                        .filter((name) => name.length > 0);
                    if (linkedNames.length > 0) {
                        return linkedNames.join(', ');
                    }
                    return row.projects ?? '';
                },
                id: 'projects',
                header: t('externalAssets.columns.projects'),
                filterFn: searchMode,
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>();
                    return (
                        <Tooltip title={value ? t('externalAssets.copyHint', { defaultValue: 'Click to copy' }) : ''}>
                            <Box
                                component="button"
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void copyValueToClipboard(value);
                                }}
                                sx={{
                                    border: 'none',
                                    background: 'transparent',
                                    p: 0,
                                    m: 0,
                                    font: 'inherit',
                                    color: 'primary.main',
                                    textAlign: 'left',
                                    cursor: value ? 'copy' : 'default',
                                    '&:hover': {
                                        textDecoration: value ? 'underline' : 'none',
                                    },
                                }}
                            >
                                {value || '-'}
                            </Box>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'tags',
                header: t('externalAssets.columns.tags'),
                filterFn: searchMode,
            },
        ],
        [copyValueToClipboard, publisherFilterFn, searchByPublisher, searchMode, t],
    );

    const sortMatchesEditPreset =
        sorting.length === 1 && sorting[0].id === 'updated_at' && sorting[0].desc === true;
    const sortMatchesTitlePreset =
        sorting.length === 1 && sorting[0].id === 'title' && sorting[0].desc === false;

    const table = useMaterialReactTable({
        columns,
        data: filteredAssets,
        enableGlobalFilter: true,
        enableColumnFilters: true,
        globalFilterFn: searchMode,
        enableRowActions: true,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        positionActionsColumn: 'last',
        initialState: {
            showColumnFilters: true,
            pagination: { pageIndex: 0, pageSize: 20 },
        },
        muiTableContainerProps: {
            ref: tableContainerRef,
            onMouseMove: (event: React.MouseEvent<HTMLDivElement>) => {
                const container = tableContainerRef.current;
                if (!container) {
                    return;
                }

                const rect = container.getBoundingClientRect();
                const x = event.clientX - rect.left;
                const edgeThreshold = 56;

                if (x >= rect.width - edgeThreshold) {
                    setHorizontalScrollDirection(1);
                    return;
                }

                if (x <= edgeThreshold) {
                    setHorizontalScrollDirection(-1);
                    return;
                }

                if (horizontalScrollDirection !== 0) {
                    setHorizontalScrollDirection(0);
                }
            },
            onMouseLeave: () => setHorizontalScrollDirection(0),
            sx: { overflowX: 'auto' },
        },
        muiToolbarAlertBannerProps: isError
            ? {
                color: 'error',
                children: t('common.errorFetchingData'),
            }
            : undefined,
        renderTopToolbarCustomActions: () => (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleOpenCreate}>
                    {t('externalAssets.createNew')}
                </Button>
                <Box
                    sx={{
                        position: 'relative',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.25,
                        py: 0.75,
                        pt: 1.25,
                    }}
                >
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{
                            position: 'absolute',
                            top: -10,
                            left: 12,
                            px: 0.75,
                            lineHeight: 1,
                            color: 'primary.main',
                            bgcolor: 'background.paper',
                            pointerEvents: 'none',
                        }}
                    >
                        {t('externalAssets.orderBy')}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Button
                            type="button"
                            size="small"
                            variant={sortMatchesEditPreset ? 'contained' : 'outlined'}
                            onClick={() => setSorting([{ id: 'updated_at', desc: true }])}
                        >
                            {t('externalAssets.sortByEdit')}
                        </Button>
                        <Button
                            type="button"
                            size="small"
                            variant={sortMatchesTitlePreset ? 'contained' : 'outlined'}
                            onClick={() => setSorting([{ id: 'title', desc: false }])}
                        >
                            {t('externalAssets.sortByTitle')}
                        </Button>
                    </Stack>
                </Box>
                <Tooltip arrow title={t('common.refreshData')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="external-assets-search-mode-label">Search mode</InputLabel>
                    <Select
                        labelId="external-assets-search-mode-label"
                        id="external-assets-search-mode"
                        value={searchMode}
                        label="Search mode"
                        onChange={(event) => setSearchMode(event.target.value as 'contains' | 'fuzzy' | 'startsWith')}
                    >
                        <MenuItem value="contains">Exact (contains)</MenuItem>
                        <MenuItem value="fuzzy">Fuzzy</MenuItem>
                        <MenuItem value="startsWith">Starts with</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        ),
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={t('common.open', { defaultValue: 'Open' })}>
                    <IconButton
                        onClick={(event) => {
                            event.stopPropagation();
                            openAssetInNewTab(row.original);
                        }}
                    >
                        <OpenInNewIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('common.edit')}>
                    <IconButton
                        onClick={(event) => {
                            event.stopPropagation();
                            handleOpenEdit(row.original);
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('common.delete')}>
                    <IconButton
                        color="error"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDelete(row.original);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        muiTableBodyRowProps: ({ row }) => ({
            onClick: (event) => {
                if (event.ctrlKey || event.metaKey) {
                    event.preventDefault();
                    openAssetInNewTab(row.original);
                    return;
                }

                handleOpenEdit(row.original);
            },
            sx: { cursor: 'pointer' },
        }),
        state: {
            columnFilters,
            globalFilter,
            sorting,
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isRefetching && !isLoading,
        },
    });

    return (
        <Box sx={{ p: 3, position: 'relative' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {t('externalAssets.title')}
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 720 }}>
                {t('externalAssets.dropLinkHint')}
            </Typography>

            <Box
                ref={dropZoneRef}
                role="region"
                tabIndex={0}
                aria-label={t('externalAssets.dropLinkZone')}
                onDragOver={handleDropZoneDragOver}
                onContextMenu={handleDropZoneContextMenu}
                onPaste={handleDropZonePaste}
                sx={{
                    mb: 2,
                    maxWidth: 720,
                    minHeight: 72,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 2,
                    borderRadius: 1,
                    border: '2px dashed',
                    borderColor: isDropZoneActive || isLinkDropOver ? 'primary.main' : 'divider',
                    bgcolor: isDropZoneActive
                        ? 'rgba(25, 118, 210, 0.14)'
                        : isLinkDropOver
                            ? 'rgba(25, 118, 210, 0.06)'
                            : 'action.hover',
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                    cursor: 'context-menu',
                    outline: 'none',
                    '&:focus-visible': {
                        borderColor: 'primary.main',
                        boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}33`,
                    },
                }}
            >
                <Typography
                    variant="body1"
                    color={isDropZoneActive ? 'primary' : 'text.secondary'}
                    sx={{ textAlign: 'center', fontWeight: isDropZoneActive ? 600 : 400, pointerEvents: 'none' }}
                >
                    {isReadingClipboard
                        ? t('externalAssets.pasteLinkWorking', { defaultValue: 'Creating asset…' })
                        : isDropZoneActive
                            ? t('externalAssets.dropLinkZoneActive')
                            : t('externalAssets.dropLinkZone')}
                </Typography>
            </Box>

            <TextField
                fullWidth
                label={t('common.search', { defaultValue: 'Search' })}
                placeholder={t('externalAssets.searchAllColumns', { defaultValue: 'Search across all columns...' })}
                value={globalFilter ?? ''}
                onChange={(event) => setGlobalFilter(event.target.value)}
                inputRef={searchInputRef}
                sx={{ mb: 2, maxWidth: 640 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment: globalFilter ? (
                        <InputAdornment position="end">
                            <Tooltip title={t('common.clear', { defaultValue: 'Clear' })}>
                                <IconButton
                                    size="small"
                                    edge="end"
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                    }}
                                    onClick={() => {
                                        setGlobalFilter('');
                                        requestAnimationFrame(() => {
                                            searchInputRef.current?.focus();
                                        });
                                    }}
                                    aria-label="Clear search"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ) : undefined,
                }}
            />

            <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {t('externalAssets.frequentSearchTags')}
                </Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {EXTERNAL_ASSETS_FREQUENT_SEARCH_TAGS.map((tag) => {
                        const active = (globalFilter ?? '').trim() === tag;
                        return (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                clickable
                                color={active ? 'primary' : 'default'}
                                variant={active ? 'filled' : 'outlined'}
                                onClick={() => {
                                    setGlobalFilter(active ? '' : tag);
                                    requestAnimationFrame(() => {
                                        searchInputRef.current?.focus();
                                    });
                                }}
                            />
                        );
                    })}
                </Stack>
            </Box>

            <MaterialReactTable table={table} />

            <Dialog
                open={isModalOpen}
                onClose={(_: object, _reason: 'backdropClick' | 'escapeKeyDown') => {
                    if (isAutoSaveBusy) {
                        return;
                    }
                    void requestCloseModal();
                }}
                disableEscapeKeyDown={isAutoSaveBusy}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '80vw',
                        maxWidth: '80vw',
                    },
                }}
            >
                <DialogTitle>
                    {selected ? t('externalAssets.editTitle') : t('externalAssets.createTitle')}
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ gridColumn: isMobile ? '1 / -1' : '1 / -1' }}
                        >
                            <Tooltip title={t('common.copy', { defaultValue: 'Copy' })}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => void copyValueToClipboard(formData.title)}
                                        disabled={!formData.title?.trim()}
                                        aria-label={t('common.copy', { defaultValue: 'Copy' })}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {selected?.id && (
                                <Tooltip
                                    title={t('externalAssets.openAssetPage', {
                                        defaultValue: 'Open asset page',
                                    })}
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            component="a"
                                            href={buildExternalAssetPageUrl(selected.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={t('externalAssets.openAssetPage', {
                                                defaultValue: 'Open asset page',
                                            })}
                                        >
                                            <LinkIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <MarkerHighlightTextField
                                label={t('externalAssets.columns.title')}
                                value={formData.title}
                                onChange={(event) => setFormData({ ...formData, title: event.target.value })}
                                onBlur={() => triggerAutoSaveNow()}
                                error={!!errors.title}
                                helperText={errors.title}
                                required
                                fullWidth
                            />
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ gridColumn: '1 / -1' }}
                        >
                            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                <Tooltip title={t('common.copy', { defaultValue: 'Copy' })}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => void copyValueToClipboard(formData.url)}
                                            disabled={!formData.url?.trim()}
                                            aria-label={t('common.copy', { defaultValue: 'Copy' })}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('common.open', { defaultValue: 'Open link' })}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            component="a"
                                            href={normalizedFormUrl || undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            disabled={!formData.url?.trim()}
                                            aria-label={t('common.open', { defaultValue: 'Open link' })}
                                        >
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                            <TextField
                                label={t('externalAssets.columns.url')}
                                value={formData.url}
                                onChange={(event) => setFormData({ ...formData, url: event.target.value })}
                                onBlur={() => triggerAutoSaveNow()}
                                fullWidth
                            />
                        </Stack>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <RichHtmlEditorField
                                label={t('externalAssets.columns.info')}
                                helperText={t('externalAssets.infoHint')}
                                value={formData.info ?? ''}
                                onChange={(next) => setFormData((prev) => ({ ...prev, info: next }))}
                                onBlur={(next) => {
                                    const nextData = { ...formData, info: next };
                                    setFormData(nextData);
                                    triggerAutoSaveNow(nextData);
                                }}
                                editorModeStorageKey="externalAssets.info.editorMode"
                                showHtmlSourceStorageKey="externalAssets.info.showHtmlSource"
                                paneHeight={220}
                                joditMinHeight={220}
                                defaultEditorMode="jodit"
                                showJoditQuickActions={false}
                            />
                        </Box>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <FilePathEntriesEditor
                                entries={filePathRows}
                                onChange={updateFilePathRows}
                                title={t('externalAssets.columns.filePath')}
                                pathPlaceholder="I:\\My Drive\\FabAssets\\…"
                                createEmptyEntry={createDefaultFilePathEntry}
                                onBlur={() => triggerAutoSaveNow()}
                                onCopy={copyValueToClipboard}
                            />
                        </Box>
                        <MarkerHighlightTextField
                            label={t('externalAssets.columns.publisher')}
                            value={formData.publisher}
                            onChange={(event) => setFormData({ ...formData, publisher: event.target.value })}
                            onBlur={() => triggerAutoSaveNow()}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip
                                            title={t('externalAssets.searchByPublisher', {
                                                defaultValue: 'Search assets by this publisher',
                                            })}
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    edge="end"
                                                    color={normalizedFormPublisherUrl ? 'default' : 'primary'}
                                                    onClick={() => searchByPublisher(formData.publisher ?? '', { closeModal: true })}
                                                    disabled={!extractPublisherSearchTerm(formData.publisher ?? '')}
                                                    aria-label={t('externalAssets.searchByPublisher', {
                                                        defaultValue: 'Search assets by this publisher',
                                                    })}
                                                >
                                                    <SearchIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip
                                            title={
                                                normalizedFormPublisherUrl
                                                    ? t('common.open', { defaultValue: 'Open link' })
                                                    : t('externalAssets.publisherNoLink', { defaultValue: 'No publisher link' })
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    edge="end"
                                                    component="a"
                                                    href={normalizedFormPublisherUrl || undefined}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    disabled={!normalizedFormPublisherUrl}
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label={t('externalAssets.columns.source')}
                            value={formData.source}
                            onChange={(event) => setFormData({ ...formData, source: event.target.value })}
                            onBlur={() => triggerAutoSaveNow()}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="external-asset-import-type-label">
                                {t('externalAssets.columns.type')}
                            </InputLabel>
                            <Select
                                labelId="external-asset-import-type-label"
                                label={t('externalAssets.columns.type')}
                                value={formData.asset_type || 'auto'}
                                onChange={(event) => {
                                    setFormData({ ...formData, asset_type: event.target.value });
                                    triggerAutoSaveNow({ ...formData, asset_type: event.target.value });
                                }}
                            >
                                {importTypeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Autocomplete
                            options={productTypeOptions}
                            value={
                                productTypeOptions.find((option) => option.id === (formData.product_type_id ?? ''))
                                ?? null
                            }
                            onChange={(_, option) => {
                                const next = { ...formData, product_type_id: option?.id ?? '' };
                                setFormData(next);
                                triggerAutoSaveNow(next);
                            }}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(left, right) => left.id === right.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('externalAssets.columns.productType', { defaultValue: 'Product type (Fab)' })}
                                />
                            )}
                            fullWidth
                        />
                        <Autocomplete
                            freeSolo
                            fullWidth
                            options={statusOptions}
                            value={formData.status}
                            onChange={(_event, value) => {
                                const next = { ...formData, status: value ?? '' };
                                setFormData(next);
                                triggerAutoSaveNow(next);
                            }}
                            onInputChange={(_event, value, reason) => {
                                if (reason === 'input' || reason === 'clear') {
                                    setFormData({ ...formData, status: value });
                                }
                            }}
                            onBlur={() => triggerAutoSaveNow()}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('externalAssets.columns.status')}
                                    placeholder="Owned, AsiNepouzijem…"
                                />
                            )}
                        />
                        <Autocomplete<string, true, false, true>
                            multiple
                            freeSolo
                            fullWidth
                            options={tagAutocompleteOptions}
                            value={parseAssetTagString(formData.tags)}
                            onChange={(_, newValue) => {
                                const expanded: string[] = [];
                                const seen = new Set<string>();
                                for (const v of newValue) {
                                    const s = typeof v === 'string' ? v.trim() : String(v).trim();
                                    if (!s) {
                                        continue;
                                    }
                                    for (const part of parseAssetTagString(s)) {
                                        if (!seen.has(part)) {
                                            seen.add(part);
                                            expanded.push(part);
                                        }
                                    }
                                }
                                setFormData({ ...formData, tags: serializeAssetTags(expanded) });
                            }}
                            onBlur={() => triggerAutoSaveNow()}
                            isOptionEqualToValue={(option, value) => option === value}
                            filterSelectedOptions
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={`${option}-${index}`}
                                        label={option}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('externalAssets.columns.tags')}
                                    placeholder={t('externalAssets.tagsPlaceholder', {
                                        defaultValue: 'Choose or type — Enter to add',
                                    })}
                                    helperText={t('externalAssets.tagsHelper', {
                                        defaultValue: 'Comma, semicolon, or new line also work when pasted.',
                                    })}
                                />
                            )}
                        />
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={availableProjects}
                            loading={projectsCatalogLoading}
                            getOptionLabel={(option) => option?.name || ''}
                            value={selectedProjects}
                            onChange={(_event, value) =>
                                setFormData({
                                    ...formData,
                                    project_ids: (value || []).map((project) => project.id),
                                })
                            }
                            filterSelectedOptions
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                                return (
                                    <li key={key} {...rest}>
                                        <Checkbox checked={selected} />
                                        <ListItemText primary={option.name} />
                                    </li>
                                );
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((project, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={project.id}
                                        label={project.name || project.id}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('externalAssets.columns.projects')}
                                    placeholder={t('externalAssets.projectsPlaceholder', {
                                        defaultValue: 'Start typing project name',
                                    })}
                                    helperText={
                                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" component="span">
                                            <span>
                                                {t('externalAssets.projectsEditableHint', {
                                                    defaultValue: 'Link this asset to one or more projects. Changes sync both ways.',
                                                })}
                                            </span>
                                            {selectedProjects.map((project) => (
                                                <Button
                                                    key={project.id}
                                                    size="small"
                                                    variant="outlined"
                                                    endIcon={<OpenInNewIcon fontSize="small" />}
                                                    href={`/projects?projectId=${encodeURIComponent(project.id)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ textTransform: 'none', py: 0, lineHeight: 1.5 }}
                                                    component="a"
                                                >
                                                    {project.name}
                                                </Button>
                                            ))}
                                        </Stack>
                                    }
                                />
                            )}
                        />
                        <Box
                            sx={{
                                gridColumn: isMobile ? '1 / -1' : '1 / -1',
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                gap: 2,
                                alignItems: 'start',
                            }}
                        >
                            <Box>
                                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                        {t('externalAssets.columns.notes')}
                                    </Typography>
                                </Box>
                                <Stack spacing={1} sx={{ mb: 1.25 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                            Quick actions
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={insertNotesTimestamp}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Insert time (Alt+Q)
                                        </Button>
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                            Text color
                                        </Typography>
                                        {notesQuickColorOptions.map((item) => (
                                            <Button
                                                key={`text-${item.key}`}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => applyNotesQuickColor('text', item.key)}
                                                startIcon={
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            bgcolor: notesQuickPalette.text[item.key],
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                        }}
                                                    />
                                                }
                                                sx={{
                                                    textTransform: 'none',
                                                    color: notesQuickPalette.text[item.key],
                                                    borderColor: notesQuickPalette.text[item.key],
                                                }}
                                            >
                                                {item.label} (Alt+Shift+{item.hotkey})
                                            </Button>
                                        ))}
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="caption" sx={{ minWidth: 72, color: 'text.secondary' }}>
                                            Background
                                        </Typography>
                                        {notesQuickColorOptions.map((item) => (
                                            <Button
                                                key={`background-${item.key}`}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => applyNotesQuickColor('background', item.key)}
                                                startIcon={
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            bgcolor: notesQuickPalette.background[item.key],
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                        }}
                                                    />
                                                }
                                                sx={{
                                                    textTransform: 'none',
                                                    bgcolor: notesQuickPalette.background[item.key],
                                                    borderColor: notesQuickPalette.text[item.key],
                                                    color: '#1f1f1f',
                                                    '&:hover': {
                                                        bgcolor: notesQuickPalette.background[item.key],
                                                        filter: 'brightness(0.98)',
                                                    },
                                                }}
                                            >
                                                {item.label} (Alt+{item.hotkey})
                                            </Button>
                                        ))}
                                    </Stack>
                                </Stack>
                                <JoditEditor
                                    ref={notesEditorRef}
                                    value={formData.notes ?? ''}
                                    config={notesEditorConfig}
                                    onBlur={(content) => {
                                        const nextData = { ...formData, notes: content };
                                        setFormData(nextData);
                                        triggerAutoSaveNow(nextData);
                                    }}
                                    onChange={() => {
                                        // Keep Jodit internally uncontrolled while typing to prevent cursor jumps.
                                    }}
                                />
                            </Box>

                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                                        {t('externalAssets.notesHtmlPreview', { defaultValue: 'HTML Preview' })}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {previewCandidates.length > 0 && (
                                            <Button size="small" variant="text" onClick={handleOpenPreviewLink}>
                                                Open Large Preview
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            variant={notesPreviewMode === 'rendered' ? 'contained' : 'outlined'}
                                            onClick={() => setNotesPreviewMode('rendered')}
                                        >
                                            Rendered
                                        </Button>
                                        <Button
                                            size="small"
                                            variant={notesPreviewMode === 'raw' ? 'contained' : 'outlined'}
                                            onClick={() => setNotesPreviewMode('raw')}
                                        >
                                            Raw HTML
                                        </Button>
                                    </Box>
                                </Box>
                                {notesPreviewMode === 'rendered' ? (
                                    <Box
                                        ref={notesPreviewRef}
                                        onMouseUp={updateNotesPreviewSelection}
                                        onKeyUp={updateNotesPreviewSelection}
                                        onMouseDown={() => {
                                            hideNotesPreviewSelection();
                                        }}
                                        sx={{
                                            position: 'relative',
                                            minHeight: 290,
                                            maxHeight: 360,
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            bgcolor: 'background.paper',
                                            wordBreak: 'break-word',
                                            '& a': {
                                                color: 'primary.main',
                                            },
                                            '& img': {
                                                maxWidth: '100%',
                                                height: 'auto',
                                                display: 'block',
                                            },
                                            '& table': {
                                                maxWidth: '100%',
                                                display: 'block',
                                                overflowX: 'auto',
                                            },
                                            '& pre, & code': {
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                            },
                                        }}
                                    >
                                        {notesPreviewSelection && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<ContentCopyIcon fontSize="small" />}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                }}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    void copyValueToClipboard(notesPreviewSelection.text);
                                                    hideNotesPreviewSelection();
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: Math.max(notesPreviewSelection.top, 8),
                                                    left: `clamp(8px, calc(${notesPreviewSelection.left}px - 56px), calc(100% - 104px))`,
                                                    zIndex: 2,
                                                    minWidth: 88,
                                                    boxShadow: 3,
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        )}
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    (formData.notes ?? '').trim() ||
                                                    '<span style="color:#888;">No HTML content to preview.</span>',
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <TextField
                                        value={formData.notes ?? ''}
                                        fullWidth
                                        multiline
                                        minRows={12}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                            },
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    {isModalOpen && (
                        <Typography
                            variant="caption"
                            sx={{
                                mr: 'auto',
                                px: 1.25,
                                py: 0.4,
                                borderRadius: 1,
                                fontWeight: 600,
                                color: autoSaveStatus === 'error' ? 'error.dark' : 'text.primary',
                                bgcolor:
                                    autoSaveStatus === 'pending'
                                        ? 'warning.light'
                                        : autoSaveStatus === 'saving'
                                            ? 'info.light'
                                            : autoSaveStatus === 'saved'
                                                ? 'success.light'
                                                : autoSaveStatus === 'error'
                                                    ? 'error.light'
                                                    : 'action.hover',
                            }}
                        >
                            {autoSaveStatus === 'pending' && 'Autosave pending...'}
                            {autoSaveStatus === 'saving' && 'Autosaving...'}
                            {autoSaveStatus === 'saved' && 'Saved ✓'}
                            {autoSaveStatus === 'error' && 'Autosave failed'}
                            {autoSaveStatus === 'idle' && 'Autosave ready'}
                        </Typography>
                    )}
                    <Button onClick={() => void requestCloseModal()} disabled={isAutoSaveBusy}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending}
                    >
                        {selected ? t('common.save') : t('common.create')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isPreviewModalOpen}
                onClose={handleClosePreviewModal}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '90vw',
                        maxWidth: '90vw',
                    },
                }}
            >
                <DialogTitle>
                    {t('externalAssets.notesHtmlPreview', { defaultValue: 'HTML Preview' })}
                    {previewCandidates.length > 1 ? ` (${selectedPreviewIndex + 1}/${previewCandidates.length})` : ''}
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {previewEmbedUrl ? (
                        <Box sx={{ height: '80vh', bgcolor: 'black' }}>
                            <iframe
                                src={previewEmbedUrl}
                                title="External asset preview"
                                style={{ width: '100%', height: '100%', border: 0 }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                                allowFullScreen
                            />
                        </Box>
                    ) : (
                        <Box sx={{ p: 2 }}>
                            <Typography color="text.secondary">
                                No preview URL found in Notes.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {previewCandidates.length > 1 && (
                        <>
                            <Button
                                onClick={() =>
                                    setSelectedPreviewIndex((prev) =>
                                        prev > 0 ? prev - 1 : previewCandidates.length - 1
                                    )
                                }
                            >
                                Previous
                            </Button>
                            <Button
                                onClick={() =>
                                    setSelectedPreviewIndex((prev) =>
                                        prev < previewCandidates.length - 1 ? prev + 1 : 0
                                    )
                                }
                            >
                                Next
                            </Button>
                        </>
                    )}
                    <Button onClick={handleClosePreviewModal}>{t('common.close', { defaultValue: 'Close' })}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeleteConfirmationInput('');
                }}
            >
                <DialogTitle>{t('externalAssets.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('externalAssets.deleteConfirmation')}</Typography>
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        {t('externalAssets.deleteTypeToConfirm', {
                            value: selected?.title ?? '',
                            defaultValue: 'Type "{{value}}" to confirm delete.',
                        })}
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        fullWidth
                        value={deleteConfirmationInput}
                        onChange={(event) => setDeleteConfirmationInput(event.target.value)}
                        placeholder={selected?.title ?? ''}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsDeleteModalOpen(false);
                            setDeleteConfirmationInput('');
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={
                            deleteMutation.isPending ||
                            deleteConfirmationInput.trim() !== (selected?.title ?? '').trim()
                        }
                    >
                        {t('common.yes')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
